// src/push/subscribePush.js

// Public VAPID key from frontend/.env
//   REACT_APP_VAPID_PUBLIC_KEY=BAA... (URL-safe base64)
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

/** Convert a URL-safe base64 string to a Uint8Array for PushManager */
function urlBase64ToUint8Array(base64) {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

/** True if running on an iOS device (Safari) */
function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/** True if the app is launched in standalone/PWA mode */
function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/**
 * Ensure the browser is subscribed for Web Push.
 * - Requests Notification permission if needed
 * - Subscribes with the service worker's PushManager
 * - POSTs the subscription JSON to /api/push/subscribe
 *
 * Returns the PushSubscription or undefined.
 */
export async function ensurePushSubscription() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.warn("[push] Service Worker / Push not supported in this browser.");
      return;
    }

    if (!VAPID_PUBLIC_KEY) {
      console.warn("[push] REACT_APP_VAPID_PUBLIC_KEY is missing.");
      return;
    }

    // iOS Safari only delivers push for installed PWAs
    if (isIOS() && !isStandalone()) {
      console.info("[push] iOS detected: install to Home Screen to enable push.");
      return;
    }

    // Ask for permission (gracefully handle Safari's callback-style too)
    let permission = Notification.permission;
    if (permission === "default") {
      try {
        permission = await Notification.requestPermission();
      } catch {
        // Older Safari fallback
        permission = await new Promise((resolve) =>
          Notification.requestPermission((p) => resolve(p))
        );
      }
    }
    if (permission !== "granted") {
      console.info("[push] Notifications not granted.");
      return;
    }

    // SW ready
    const reg = await navigator.serviceWorker.ready;

    // Existing subscription?
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      try {
        await fetch("/api/push/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(existing.toJSON()),
        });
      } catch (e) {
        console.warn("[push] Failed to sync existing subscription:", e);
      }
      return existing;
    }

    // Subscribe
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });

    // Persist to backend
    try {
      await fetch("/api/push/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sub.toJSON()),
      });
    } catch (e) {
      console.warn("[push] Failed to register subscription on server:", e);
    }

    return sub;
  } catch (err) {
    console.warn("[push] Subscription failed:", err);
  }
}

/**
 * Optional helper: remove the current push subscription and inform backend.
 * Your server can no-op if /api/push/unsubscribe is not implemented.
 */
export async function removePushSubscription() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;

    try {
      await fetch("/api/push/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sub.toJSON()),
      });
    } catch (e) {
      console.warn("[push] Failed to unregister on server:", e);
    }

    await sub.unsubscribe();
  } catch (err) {
    console.warn("[push] Unsubscribe failed:", err);
  }
}

export default ensurePushSubscription;

